import { decodeHtml } from '../lib/planningCycleLabels';
import type { IO, PlanLineItem } from '../types/io';
import type {
  Team,
  Department,
  DetailSheet,
  CostCenter,
  GL,
  Vendor,
  Role,
  UserType,
} from '../types/admin';
import type { Tag } from '../types/transaction';
import type { PlanningCycle, IOPlanSummary } from '../types/planning';
import type {
  DashboardIO,
  BudgetDate,
  Announcement,
  DashboardData,
  PendingItem,
  ActivityEntry,
} from '../types/dashboard';
import type {
  IORequestSummary,
  IORequestDetail,
  IORequestEventRow,
  IOTrackingPayload,
  NewIOPayload,
  ChangeIOPayload,
} from '../types/io-request';
/** Minimal shape for T&E categories (avoids importing api/te-planning). */
export interface DecodableTECategory {
  id: number;
  parent_id: number | null;
  parent_name: string | null;
  name: string;
  is_active: boolean;
  comment_required: boolean;
  sort_order: number;
}

/** Minimal shape for IO Plans Excel export rows (avoids circular import with api/planning). */
export interface DecodableCycleLineItemExportRow {
  io_order: string | null;
  io_description: string | null;
  owner_name: string | null;
  team_name: string | null;
  detail_sheet_name: string | null;
  reporting_department: string | null;
  spend_category: string | null;
  io_comment: string | null;
  description: string | null;
  gl_code: string | null;
  gl_description: string | null;
  comments: string | null;
  cost_center_code: string | null;
  cost_center_name: string | null;
  entity_code: string | null;
  entity_name: string | null;
}

/** Preserve null vs undefined from the input field type. */
export function decodeOptional<T extends string | null | undefined>(value: T): T {
  if (value == null || value === '') return value;
  return decodeHtml(value) as T;
}

export function decodeIO(io: IO): IO {
  return {
    ...io,
    order: decodeHtml(io.order),
    description: decodeOptional(io.description),
    team_name: decodeOptional(io.team_name),
    detail_sheet_name: decodeOptional(io.detail_sheet_name),
    reporting_department: decodeOptional(io.reporting_department),
    reporting_department_effective: decodeOptional(io.reporting_department_effective),
    node1: decodeOptional(io.node1),
    node2: decodeOptional(io.node2),
    node3: decodeOptional(io.node3),
    owner_name: decodeOptional(io.owner_name),
    spend_category: decodeOptional(io.spend_category),
    reference_io_order: decodeOptional(io.reference_io_order),
    secondary_stakeholder_names: io.secondary_stakeholder_names?.map((n) => decodeHtml(n)),
    owner: io.owner
      ? {
          ...io.owner,
          full_name: decodeHtml(io.owner.full_name),
        }
      : io.owner,
  };
}

export function decodeTeam(team: Team): Team {
  return {
    ...team,
    name: decodeHtml(team.name),
    description: decodeOptional(team.description),
    department_name: decodeOptional(team.department_name),
    department_head_name: decodeOptional(team.department_head_name),
    manager_name: decodeOptional(team.manager_name),
    team_manager_name: decodeOptional(team.team_manager_name),
    children: team.children?.map(decodeTeam),
  };
}

export function decodeDepartment(dept: Department): Department {
  return {
    ...dept,
    name: decodeHtml(dept.name),
    itlt_member_name: decodeOptional(dept.itlt_member_name),
    teams: dept.teams?.map(decodeTeam),
  };
}

export function decodeDetailSheet(sheet: DetailSheet): DetailSheet {
  return { ...sheet, name: decodeHtml(sheet.name) };
}

export function decodeCostCenter(cc: CostCenter): CostCenter {
  return { ...cc, name: decodeOptional(cc.name) };
}

export function decodeGL(gl: GL): GL {
  return {
    ...gl,
    gl_name: decodeOptional(gl.gl_name),
    description: decodeOptional(gl.description),
  };
}

export function decodeVendor(vendor: Vendor): Vendor {
  return {
    ...vendor,
    vendor_name: decodeOptional(vendor.vendor_name),
  };
}

export function decodeTag<T extends { name: string; label?: string; description?: string }>(tag: T): T {
  return {
    ...tag,
    name: decodeHtml(tag.name),
    label: tag.label != null ? decodeHtml(tag.label) : tag.label,
    description: decodeOptional(tag.description) as T['description'],
  };
}

export function decodeTransactionTag(tag: Tag): Tag {
  return decodeTag(tag);
}

export function decodeRole(role: Role): Role {
  return {
    ...role,
    name: decodeHtml(role.name),
    description: decodeOptional(role.description),
  };
}

export function decodeUserType(ut: UserType): UserType {
  return {
    ...ut,
    name: decodeHtml(ut.name),
    description: decodeOptional(ut.description),
  };
}

/** Admin users list / auth user shape — free-text identity fields. */
export function decodeUser<T extends {
  full_name: string;
  email?: string;
  display_name?: string | null;
  job_title?: string | null;
  department?: string | null;
  functional_area?: string | null;
  sub_department?: string | null;
  user_type_name?: string | null;
  personal_io?: string | null;
}>(user: T): T {
  return {
    ...user,
    full_name: decodeHtml(user.full_name),
    email: user.email != null ? decodeHtml(user.email) : user.email,
    display_name: decodeOptional(user.display_name as string | null | undefined) as T['display_name'],
    job_title: decodeOptional(user.job_title as string | null | undefined) as T['job_title'],
    department: decodeOptional(user.department as string | null | undefined) as T['department'],
    functional_area: decodeOptional(user.functional_area as string | null | undefined) as T['functional_area'],
    sub_department: decodeOptional(user.sub_department as string | null | undefined) as T['sub_department'],
    user_type_name: decodeOptional(user.user_type_name as string | null | undefined) as T['user_type_name'],
    personal_io: decodeOptional(user.personal_io as string | null | undefined) as T['personal_io'],
  };
}

function decodeProfileIoRef<T extends { order: string; description?: string | null }>(io: T): T {
  return {
    ...io,
    order: decodeHtml(io.order),
    description: decodeOptional(io.description as string | null | undefined) as T['description'],
  };
}

function decodeDirectReport<T extends {
  full_name: string;
  display_name?: string | null;
  job_title?: string | null;
}>(report: T): T {
  return {
    ...report,
    full_name: decodeHtml(report.full_name),
    display_name: decodeOptional(report.display_name as string | null | undefined) as T['display_name'],
    job_title: decodeOptional(report.job_title as string | null | undefined) as T['job_title'],
  };
}

/**
 * Directory / Edit User profile — decode all free-text identity & org fields
 * (job title, department, manager labels, owned IO names, etc.).
 */
export function decodeDirectoryUserProfile<T extends {
  full_name: string;
  display_name?: string | null;
  email?: string | null;
  job_title?: string | null;
  functional_area?: string | null;
  functional_subarea?: string | null;
  subbrand?: string | null;
  nick_name?: string | null;
  company_code_text?: string | null;
  personal_io?: string | null;
  cost_center?: string | null;
  manager_name?: string | null;
  manager_job_title?: string | null;
  technical_account_type?: string | null;
  directory_status?: string | null;
  owned_ios?: Array<{ id: number; order: string; description?: string }>;
  stakeholder_ios?: Array<{ id: number; order: string; description?: string }>;
  direct_reports?: Array<{
    id: number;
    full_name: string;
    display_name?: string;
    job_title?: string;
    technical_account_type?: string;
    highest_recharge_level?: string;
  }>;
  it_services?: Array<{ service_name: string; workplace_type?: string }>;
}>(profile: T): T {
  return {
    ...profile,
    full_name: decodeHtml(profile.full_name),
    display_name: decodeOptional(profile.display_name as string | null | undefined) as T['display_name'],
    email: decodeOptional(profile.email as string | null | undefined) as T['email'],
    job_title: decodeOptional(profile.job_title as string | null | undefined) as T['job_title'],
    functional_area: decodeOptional(profile.functional_area as string | null | undefined) as T['functional_area'],
    functional_subarea: decodeOptional(profile.functional_subarea as string | null | undefined) as T['functional_subarea'],
    subbrand: decodeOptional(profile.subbrand as string | null | undefined) as T['subbrand'],
    nick_name: decodeOptional(profile.nick_name as string | null | undefined) as T['nick_name'],
    company_code_text: decodeOptional(profile.company_code_text as string | null | undefined) as T['company_code_text'],
    personal_io: decodeOptional(profile.personal_io as string | null | undefined) as T['personal_io'],
    cost_center: decodeOptional(profile.cost_center as string | null | undefined) as T['cost_center'],
    manager_name: decodeOptional(profile.manager_name as string | null | undefined) as T['manager_name'],
    manager_job_title: decodeOptional(profile.manager_job_title as string | null | undefined) as T['manager_job_title'],
    technical_account_type: decodeOptional(profile.technical_account_type as string | null | undefined) as T['technical_account_type'],
    directory_status: decodeOptional(profile.directory_status as string | null | undefined) as T['directory_status'],
    owned_ios: profile.owned_ios?.map(decodeProfileIoRef),
    stakeholder_ios: profile.stakeholder_ios?.map(decodeProfileIoRef),
    direct_reports: profile.direct_reports?.map(decodeDirectReport),
    it_services: profile.it_services?.map((s) => ({
      ...s,
      service_name: decodeHtml(s.service_name),
      workplace_type: decodeOptional(s.workplace_type),
    })),
  };
}

export function decodeTECategory<T extends DecodableTECategory>(cat: T): T {
  return {
    ...cat,
    name: decodeHtml(cat.name),
    parent_name: decodeOptional(cat.parent_name) ?? null,
  };
}

export function decodePlanningCycle(cycle: PlanningCycle): PlanningCycle {
  return {
    ...cycle,
    description: decodeOptional(cycle.description),
  };
}

export function decodeKeyDate(date: BudgetDate): BudgetDate {
  return {
    ...date,
    title: decodeHtml(date.title),
    description: decodeOptional(date.description),
    cycle_name: decodeOptional(date.cycle_name) ?? null,
  };
}

export function decodeAnnouncement(a: Announcement): Announcement {
  return {
    ...a,
    title: decodeHtml(a.title),
    message: decodeHtml(a.message),
    created_by_name: decodeOptional(a.created_by_name),
  };
}

export function decodeDashboardIO(io: DashboardIO): DashboardIO {
  return {
    ...io,
    order: decodeHtml(io.order),
    description: decodeOptional(io.description),
    owner_name: decodeOptional(io.owner_name) ?? null,
    cycle_name: decodeOptional(io.cycle_name),
  };
}

export function decodePendingItem(item: PendingItem): PendingItem {
  return {
    ...item,
    message: decodeHtml(item.message),
    io_order: decodeOptional(item.io_order),
  };
}

export function decodeActivityEntry(entry: ActivityEntry): ActivityEntry {
  return {
    ...entry,
    summary: decodeHtml(entry.summary),
  };
}

export function decodeDashboardData(data: DashboardData): DashboardData {
  return {
    ...data,
    announcements: data.announcements?.map(decodeAnnouncement) ?? [],
    upcoming_dates: data.upcoming_dates?.map(decodeKeyDate) ?? [],
    later_dates: data.later_dates?.map(decodeKeyDate) ?? [],
    my_ios: data.my_ios?.map(decodeDashboardIO) ?? [],
    pending_items: data.pending_items?.map(decodePendingItem) ?? [],
    current_cycle: data.current_cycle ? decodePlanningCycle(data.current_cycle) : null,
    my_activity: data.my_activity?.map(decodeActivityEntry) ?? [],
  };
}

function decodeWarning(
  w: { code?: string; type?: string; message: string; severity: string },
): { code?: string; type?: string; message: string; severity: string } {
  return { ...w, message: decodeHtml(w.message) };
}

export function decodeIOPlanSummary(s: IOPlanSummary): IOPlanSummary {
  return {
    ...s,
    io_order: decodeHtml(s.io_order),
    io_description: decodeOptional(s.io_description),
    owner_name: decodeOptional(s.owner_name),
    team_name: decodeOptional(s.team_name),
    detail_sheet_name: decodeOptional(s.detail_sheet_name) ?? null,
    reporting_department: decodeOptional(s.reporting_department) ?? null,
    spend_category: decodeOptional(s.spend_category),
    io_comment: decodeOptional(s.io_comment),
    prev_cycle_name: decodeOptional(s.prev_cycle_name),
    prev_cycle_display_name: decodeOptional(s.prev_cycle_display_name),
    submitted_by_name: decodeOptional(s.submitted_by_name) ?? null,
    locked_by_name: decodeOptional(s.locked_by_name) ?? null,
    warnings: s.warnings?.map(decodeWarning),
    warnings_all: s.warnings_all?.map(decodeWarning),
  };
}

export function decodePlanLineItem(item: PlanLineItem): PlanLineItem {
  return {
    ...item,
    description: decodeOptional(item.description),
    gl_code: decodeOptional(item.gl_code),
    gl_name: decodeOptional(item.gl_name),
    gl_description: decodeOptional(item.gl_description),
    comments: decodeOptional(item.comments),
  };
}

export function decodeCycleLineItemExportRow<T extends DecodableCycleLineItemExportRow>(row: T): T {
  return {
    ...row,
    io_order: decodeOptional(row.io_order) ?? null,
    io_description: decodeOptional(row.io_description) ?? null,
    owner_name: decodeOptional(row.owner_name) ?? null,
    team_name: decodeOptional(row.team_name) ?? null,
    detail_sheet_name: decodeOptional(row.detail_sheet_name) ?? null,
    reporting_department: decodeOptional(row.reporting_department) ?? null,
    spend_category: decodeOptional(row.spend_category) ?? null,
    io_comment: decodeOptional(row.io_comment) ?? null,
    description: decodeOptional(row.description) ?? null,
    gl_code: decodeOptional(row.gl_code) ?? null,
    gl_description: decodeOptional(row.gl_description) ?? null,
    comments: decodeOptional(row.comments) ?? null,
    cost_center_code: decodeOptional(row.cost_center_code) ?? null,
    cost_center_name: decodeOptional(row.cost_center_name) ?? null,
    entity_code: decodeOptional(row.entity_code) ?? null,
    entity_name: decodeOptional(row.entity_name) ?? null,
  };
}

function decodeIOTrackingPayload(payload: IOTrackingPayload | Record<string, unknown>): IOTrackingPayload | Record<string, unknown> {
  if (!payload || typeof payload !== 'object') return payload;
  const formType = (payload as { form_type?: string }).form_type;
  if (formType === 'new_io') {
    const p = payload as NewIOPayload;
    return {
      ...p,
      description: decodeHtml(p.description),
      budget_owner: decodeHtml(p.budget_owner),
      budget_area: decodeOptional(p.budget_area),
    };
  }
  if (formType === 'change_io') {
    const p = payload as ChangeIOPayload;
    return {
      ...p,
      technical_name: decodeHtml(p.technical_name),
      new_description: decodeHtml(p.new_description),
      new_owner: decodeHtml(p.new_owner),
    };
  }
  return payload;
}

function decodeIORequestEvent(event: IORequestEventRow): IORequestEventRow {
  return {
    ...event,
    actor_name: decodeHtml(event.actor_name),
    comment: decodeOptional(event.comment) ?? null,
  };
}

export function decodeIORequestSummary(req: IORequestSummary): IORequestSummary {
  return {
    ...req,
    subject: decodeHtml(req.subject),
    submitted_by_name: decodeOptional(req.submitted_by_name) ?? null,
    manager_name: decodeOptional(req.manager_name) ?? null,
    manager_action_by_name: decodeOptional(req.manager_action_by_name) ?? null,
    manager_comment: decodeOptional(req.manager_comment) ?? null,
    finance_action_by_name: decodeOptional(req.finance_action_by_name) ?? null,
    finance_comment: decodeOptional(req.finance_comment) ?? null,
  };
}

export function decodeIORequestDetail(req: IORequestDetail): IORequestDetail {
  const labels = req.labels
    ? {
        users: Object.fromEntries(
          Object.entries(req.labels.users ?? {}).map(([k, v]) => [k, decodeHtml(v)]),
        ),
      }
    : req.labels;
  return {
    ...decodeIORequestSummary(req),
    payload: decodeIOTrackingPayload(req.payload),
    events: req.events?.map(decodeIORequestEvent) ?? [],
    labels,
  };
}

/** Admin pull-forward entry (free-text vendor / cost center / comment). */
export interface AdminPullForwardEntry {
  id: number;
  item_description?: string | null;
  vendor?: string | null;
  cost_center?: string | null;
  comment?: string | null;
  status?: string | null;
  target_cycle?: string | null;
  [key: string]: unknown;
}

export function decodeAdminPullForwardEntry<T extends AdminPullForwardEntry>(entry: T): T {
  return {
    ...entry,
    item_description: decodeOptional(entry.item_description as string | null | undefined) ?? null,
    vendor: decodeOptional(entry.vendor as string | null | undefined) ?? null,
    cost_center: decodeOptional(entry.cost_center as string | null | undefined) ?? null,
    comment: decodeOptional(entry.comment as string | null | undefined) ?? null,
    status: decodeOptional(entry.status as string | null | undefined) ?? null,
    target_cycle: decodeOptional(entry.target_cycle as string | null | undefined) ?? null,
  };
}

export interface AdminPullForwardPlan {
  id: number;
  name?: string | null;
  description?: string | null;
  [key: string]: unknown;
}

export function decodeAdminPullForwardPlan<T extends AdminPullForwardPlan>(plan: T): T {
  return {
    ...plan,
    name: decodeOptional(plan.name as string | null | undefined) ?? null,
    description: decodeOptional(plan.description as string | null | undefined) ?? null,
  };
}

/** Admin sessions list. */
export function decodeSession<T extends { user_name?: string | null }>(session: T): T {
  return {
    ...session,
    user_name: decodeOptional(session.user_name as string | null | undefined) as T['user_name'],
  };
}

export function decodeMaintenanceStatus<T extends { message?: string | null }>(status: T): T {
  return {
    ...status,
    message: decodeOptional(status.message as string | null | undefined) as T['message'],
  };
}

/** Activity log row (admin page + API). */
export function decodeActivityLogEntry<T extends {
  user_name?: string | null;
  summary?: string | null;
  description?: string | null;
}>(entry: T): T {
  return {
    ...entry,
    user_name: decodeOptional(entry.user_name as string | null | undefined) as T['user_name'],
    summary: decodeOptional(entry.summary as string | null | undefined) as T['summary'],
    description: decodeOptional(entry.description as string | null | undefined) as T['description'],
  };
}

export function decodeActivityStats<T extends {
  most_active_users?: Array<{ user_id: number; user_name: string; count: number }>;
}>(stats: T): T {
  return {
    ...stats,
    most_active_users: stats.most_active_users?.map((u) => ({
      ...u,
      user_name: decodeHtml(u.user_name),
    })),
  };
}

/** Error log row. */
export function decodeErrorLog<T extends {
  message?: string | null;
  user_name?: string | null;
  error_type?: string | null;
}>(entry: T): T {
  return {
    ...entry,
    message: decodeOptional(entry.message as string | null | undefined) as T['message'],
    user_name: decodeOptional(entry.user_name as string | null | undefined) as T['user_name'],
    error_type: decodeOptional(entry.error_type as string | null | undefined) as T['error_type'],
  };
}

/** Security log row. */
export function decodeSecurityLog<T extends {
  user_name?: string | null;
  user_email?: string | null;
}>(entry: T): T {
  return {
    ...entry,
    user_name: decodeOptional(entry.user_name as string | null | undefined) as T['user_name'],
    user_email: decodeOptional(entry.user_email as string | null | undefined) as T['user_email'],
  };
}

/** OP recharge cost center. */
export function decodeOPCostCenter<T extends { name: string }>(cc: T): T {
  return { ...cc, name: decodeHtml(cc.name) };
}

/** Minimal shape for BI Reference GL phasing metadata (avoids circular import with api/planning). */
export interface DecodableBIReferenceGLPhasingMeta {
  comment?: string | null;
  created_by_name?: string | null;
  updated_by_name?: string | null;
}

/** Decode HTML-escaped phasing comment / actor names for display and edit. */
export function decodeBIReferenceGLPhasingMeta<T extends DecodableBIReferenceGLPhasingMeta>(meta: T): T {
  return {
    ...meta,
    comment: decodeOptional(meta.comment as string | null | undefined) as T['comment'],
    created_by_name: decodeOptional(meta.created_by_name as string | null | undefined) as T['created_by_name'],
    updated_by_name: decodeOptional(meta.updated_by_name as string | null | undefined) as T['updated_by_name'],
  };
}

function decodePhasingMetaField<T extends DecodableBIReferenceGLPhasingMeta | null | undefined>(
  meta: T,
): T {
  if (meta == null) return meta;
  return decodeBIReferenceGLPhasingMeta(meta) as T;
}

/** Decode all nested phasing comment fields on a BI comparison payload. */
export function decodeBIComparisonData<T extends {
  phasing_by_gl_month?: Record<string, DecodableBIReferenceGLPhasingMeta> | null;
  plan_compliance?: Record<string | number, { phasing_meta?: DecodableBIReferenceGLPhasingMeta | null }>;
  plan_compliance_by_gl?: Record<string, { phasing_meta?: DecodableBIReferenceGLPhasingMeta | null }>;
}>(data: T): T {
  const decodeComplianceMap = <M extends Record<string | number, { phasing_meta?: DecodableBIReferenceGLPhasingMeta | null }> | undefined>(
    map: M,
  ): M => {
    if (!map) return map;
    const out: Record<string | number, { phasing_meta?: DecodableBIReferenceGLPhasingMeta | null }> = {};
    for (const [key, entry] of Object.entries(map)) {
      out[key] = {
        ...entry,
        phasing_meta: decodePhasingMetaField(entry.phasing_meta),
      };
    }
    return out as M;
  };

  let phasingByGlMonth = data.phasing_by_gl_month;
  if (phasingByGlMonth) {
    const out: Record<string, DecodableBIReferenceGLPhasingMeta> = {};
    for (const [key, meta] of Object.entries(phasingByGlMonth)) {
      out[key] = decodeBIReferenceGLPhasingMeta(meta);
    }
    phasingByGlMonth = out;
  }

  return {
    ...data,
    phasing_by_gl_month: phasingByGlMonth as T['phasing_by_gl_month'],
    plan_compliance: decodeComplianceMap(data.plan_compliance) as T['plan_compliance'],
    plan_compliance_by_gl: decodeComplianceMap(data.plan_compliance_by_gl) as T['plan_compliance_by_gl'],
  };
}
